import {
  Box,
  Grid,
  Stack,
  Typography,
  Link,
  IconButton,
  Tooltip,
  Button,
  Paper,
  Avatar,
  Chip,
  Divider,
  Alert,
  Breadcrumbs,
  Tabs,
  Tab
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { IMAGE_URL } from "../../common/api/apiConfig";
import { useTopics } from "../../context/topics-context";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { formField, requiredField } from "../../common/constants";
import FormTextInput from "../../common/components/form/form-text-input";
import CommonPopup from "../../common/components/Dialog/popup";
import { useEffect, useState } from "react";

const ViewTopics = () => {
  const location = useLocation();
  const topicSdata = location?.state?.data;
  const { popupType, setPopupType, validateTopic, rejectTopic, onlyTopicsDetails, getTopicsById, loading } = useTopics();
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const [activePricingTab, setActivePricingTab] = useState(1);
  const { control, handleSubmit, reset, formState: { errors } } = useForm();

  const topiId = localStorage.getItem("topicId");
  const navigate = useNavigate();

  const handleApprove = () => {
    setPopupType("accept");
    setPopupOpen(true);
  };

  const handleReject = () => {
    setPopupType("reject");
    setPopupOpen(true);
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      proof_id: onlyTopicsDetails?._id,
    };
    await rejectTopic(payload);
    setPopupOpen(false);
    reset();
    navigate(-1);
  };

  useEffect(() => {
    if (topicSdata) {
      const id = topicSdata?.topic?._id;
      getTopicsById(id);
    } else {
      getTopicsById(topiId);
    }
  }, [topicSdata, topiId]);

  const duration = onlyTopicsDetails?.topic?.topic_pricing_data?.[0];
  const isPricingAvailable = onlyTopicsDetails?.topic?.topic_pricing_data?.length > 0;

  const isCallEnabled = isPricingAvailable && !!(duration?.good_chat_time_enabled || duration?.more_chat_time_enabled || duration?.most_chat_time_enabled);
  const isItineraryEnabled = isPricingAvailable && !!duration?.word_enabled;
  const isCallItineraryEnabled = isPricingAvailable && !!(duration?.good_time_enabled || duration?.more_time_enabled || duration?.most_time_enabled);

  useEffect(() => {
    if (duration) {
      if (isCallEnabled) {
        setActivePricingTab(1);
      } else if (isItineraryEnabled) {
        setActivePricingTab(2);
      } else if (isCallItineraryEnabled) {
        setActivePricingTab(3);
      }
    }
  }, [duration, isCallEnabled, isItineraryEnabled, isCallItineraryEnabled]);

  // Helper function to handle fallback values safely
  const getValue = (val) => (val !== null && val !== undefined && val !== "" ? val : "-");

  // Format Date Range
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return null;
    }
  };

  const startDate = formatDate(onlyTopicsDetails?.topic?.date_of_visit_start);
  const endDate = formatDate(onlyTopicsDetails?.topic?.date_of_visit_end);
  const dateText = startDate && endDate ? `${startDate} - ${endDate}` : (startDate || endDate || "-");

  // Format Currency Symbol
  const getCurrencySymbol = (currencyCode) => {
    if (currencyCode === "INR") return "₹";
    if (currencyCode === "USD") return "$";
    if (currencyCode === "EUR") return "€";
    if (currencyCode === "GBP") return "£";
    return currencyCode || "₹";
  };

  // Format Budget Range
  const budgetMin = onlyTopicsDetails?.topic?.budget_min;
  const budgetMax = onlyTopicsDetails?.topic?.budget_max;
  const budgetText = (budgetMin !== null && budgetMin !== undefined) && (budgetMax !== null && budgetMax !== undefined)
    ? `${budgetMin.toLocaleString()} - ${budgetMax.toLocaleString()}`
    : (budgetMin !== null && budgetMin !== undefined) ? `${budgetMin.toLocaleString()}` : "-";

  // Category Selector Helper
  const getCategoryName = () => {
    const cat = onlyTopicsDetails?.topic?.category;
    if (Array.isArray(cat) && cat.length > 0) {
      return cat[0]?.categories_name || cat[0]?.name || "-";
    }
    if (cat && typeof cat === 'object') {
      return cat.categories_name || cat.name || "-";
    }
    const catId = onlyTopicsDetails?.topic?.category_id;
    if (Array.isArray(catId) && catId.length > 0) {
      return catId[0]?.categories_name || catId[0]?.name || "-";
    }
    return "-";
  };

  // YouTube Embed URL Parser
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = "";
    if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("youtube.com/watch")) {
      try {
        const urlParams = new URLSearchParams(new URL(url).search);
        videoId = urlParams.get("v");
      } catch (e) {
        // Fallback split if URL parsing fails
        videoId = url.split("v=")[1]?.split("&")[0];
      }
    } else if (url.includes("youtube.com/embed/")) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${IMAGE_URL}${path}`;
  };

  const topicStatus = onlyTopicsDetails?.topic?.status;
  let statusLabel = "PENDING";
  let statusColor = "warning";
  if (topicStatus === "VERIFIED") {
    statusLabel = "VERIFIED";
    statusColor = "success";
  } else if (topicStatus === "VALIDATION_FAILED") {
    statusLabel = "REJECTED";
    statusColor = "error";
  }

  const showValidationFailedAlert = topicStatus === "VALIDATION_FAILED" || onlyTopicsDetails?.message || onlyTopicsDetails?.next_step;
  const advisor = onlyTopicsDetails?.topic?.user;
  const pricing = onlyTopicsDetails?.topic?.topic_pricing_data;
  const mediaList = onlyTopicsDetails?.topic?.media_data || onlyTopicsDetails?.topic?.media || [];
  const essentialItems = onlyTopicsDetails?.topic?.essential_item_data;
  const tripSchedule = onlyTopicsDetails?.topic?.trip_schedule_data;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F4F6F8", minHeight: "100vh", borderRadius: 2 }}>
      {/* Header Breadcrumbs & Actions */}
      <CardHeader
        onBack={() => navigate(-1)}
        statusLabel={statusLabel}
        statusColor={statusColor}
        isVerified={topicStatus === "VERIFIED"}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Left Column: Topic Details */}
        <Grid item xs={12} md={7.5} lg={8}>
          <Stack spacing={3}>
            {/* Validation History/Alerts */}
            {showValidationFailedAlert && (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: "12px", background: "#FFEFEF", border: "1px solid #FFCDCD" }}>
                <Typography variant="subtitle2" fontWeight={700}>Validation Remarks</Typography>
                {onlyTopicsDetails?.message && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Reason:</strong> {onlyTopicsDetails.message}
                  </Typography>
                )}
                {onlyTopicsDetails?.next_step && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Next Steps:</strong> {onlyTopicsDetails.next_step}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Main Topic Information */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ mb: 1 }}>
                {getValue(onlyTopicsDetails?.topic?.title)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Created on {onlyTopicsDetails?.topic?.createdAt ? new Date(onlyTopicsDetails.topic.createdAt).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}
              </Typography>

              <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1.5 }}>
                Description
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.7, mb: 3 }}>
                {getValue(onlyTopicsDetails?.topic?.description)}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                Quick Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem icon="mdi:map-marker-outline" label="Location" value={getValue(onlyTopicsDetails?.topic?.location)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem icon="mdi:tag-outline" label="Category" value={getCategoryName()} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem icon="mdi:wallet-outline" label="Budget" value={onlyTopicsDetails?.topic?.topic_pricing_data?.[0]?.currency ? `${getCurrencySymbol(onlyTopicsDetails.topic.topic_pricing_data[0].currency)} ${budgetText}` : budgetText} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem icon="mdi:shape-outline" label="Type" value={getValue(onlyTopicsDetails?.topic?.select_type)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem icon="mdi:share-variant-outline" label="Share Experience" value={getValue(onlyTopicsDetails?.topic?.share_exp_via)} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetaItem
                    icon="mdi:eye-outline"
                    label="Views & Votes"
                    value={`${onlyTopicsDetails?.topic?.views || 0} Views • ${onlyTopicsDetails?.topic?.votes || 0} Votes`}
                  />
                </Grid>
              </Grid>

              {dateText && dateText !== "-" && (
                <Box sx={{ mt: 2 }}>
                  <MetaItem icon="mdi:calendar-range" label="Visit Dates" value={dateText} />
                </Box>
              )}
            </Paper>

            {/* Essential Items */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Icon icon="mdi:format-list-bulleted-square" width="22" height="22" style={{ color: "#487FFF" }} />
                Essential Info & Pack Lists
              </Typography>
              {essentialItems && essentialItems.length > 0 ? (
                <Box>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      aria-label="essential-info-tabs"
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        "& .MuiTab-root": {
                          fontWeight: 600,
                          textTransform: "none",
                          fontSize: "0.875rem",
                          minHeight: 44,
                        }
                      }}
                    >
                      {essentialItems.map((item, idx) => (
                        <Tab
                          key={idx}
                          label={item.heading?.itemsToPack || `Section ${idx + 1}`}
                          id={`essential-tab-${idx}`}
                          aria-controls={`essential-tabpanel-${idx}`}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {essentialItems.map((item, idx) => (
                    <Box
                      key={idx}
                      role="tabpanel"
                      hidden={activeTab !== idx}
                      id={`essential-tabpanel-${idx}`}
                      aria-labelledby={`essential-tab-${idx}`}
                    >
                      {activeTab === idx && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2.5,
                            borderRadius: "12px",
                            border: "1px solid rgba(0,0,0,0.06)",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Box
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.875rem",
                              lineHeight: 1.65,
                              "& p": { mt: 0, mb: 1.5 },
                              "& p:last-child": { mb: 0 },
                              "& strong": { color: "text.primary", fontWeight: 600 },
                              "& ul, & ol": { mt: 0, mb: 1.5, pl: 3 },
                              "& li": { mb: 0.5 }
                            }}
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No pack lists or essential items details configured.</Typography>
              )}
            </Paper>

            {/* Trip Schedule Timeline */}
            {/* <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Icon icon="mdi:clock-outline" width="22" height="22" style={{ color: "#487FFF" }} />
                Trip Schedule / Itinerary
              </Typography>
              {tripSchedule && tripSchedule.length > 0 ? (
                <Stack spacing={2} sx={{ position: "relative", pl: { xs: 1, sm: 2 } }}>
                  {tripSchedule.map((item, idx) => {
                    const dayTitle = item.title || `Day ${item.day || item.day_number || (idx + 1)}`;
                    const timeText = item.time || item.duration || "";
                    const description = item.description || item.details || "";
                    const location = item.location || "";
                    return (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          border: "1px solid rgba(0,0,0,0.06)",
                          background: "#ffffff",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Icon icon="mdi:calendar-today" width="18" height="18" style={{ color: "#487FFF" }} />
                            <Typography variant="subtitle2" fontWeight={700}>{dayTitle}</Typography>
                          </Stack>
                          {timeText && (
                            <Chip label={timeText} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.75rem" }} />
                          )}
                        </Stack>
                        {location && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1, color: "text.secondary" }}>
                            <Icon icon="mdi:map-marker" width="14" height="14" style={{ color: "#487FFF" }} />
                            <Typography variant="caption" fontWeight={500}>{location}</Typography>
                          </Stack>
                        )}
                        {description && (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>
                            {description}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No trip schedule / day-by-day itinerary added.</Typography>
              )}
            </Paper> */}

            {/* Public Media */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Icon icon="mdi:image-multiple-outline" width="22" height="22" style={{ color: "#487FFF" }} />
                Public Topic Media
              </Typography>
              {mediaList && mediaList.length > 0 ? (
                <Grid container spacing={2}>
                  {mediaList.map((med, i) => {
                    const isImg = med.type === "IMAGE" || (typeof med === 'string' && (med.endsWith('.jpeg') || med.endsWith('.jpg') || med.endsWith('.png')));
                    const url = med.url || med;
                    return (
                      <Grid item xs={6} sm={4} md={3} key={i}>
                        <Box
                          onClick={() => isImg && setSelectedImg(getImageUrl(url))}
                          sx={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "100%",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid rgba(0,0,0,0.06)",
                            boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                            cursor: isImg ? "pointer" : "default",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.03)",
                              boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
                            }
                          }}
                        >
                          {isImg ? (
                            <Box
                              component="img"
                              src={getImageUrl(url)}
                              alt={`Topic media ${i}`}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                background: "#f0f2f5"
                              }}
                            >
                              <Icon icon="mdi:video" width="36" height="36" style={{ color: "#888" }} />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">No media files uploaded.</Typography>
              )}
            </Paper>

            {/* Validation Proofs Section */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <Icon icon="mdi:shield-check-outline" width="22" height="22" style={{ color: "#487FFF" }} />
                Validation Proofs
              </Typography>

              <Stack spacing={4}>
                {/* Videos */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ fontSize: "0.75rem", textTransform: "uppercase", mb: 1.5 }}>
                    1. Video Proofs
                  </Typography>
                  {onlyTopicsDetails?.video_link && onlyTopicsDetails.video_link.length > 0 ? (
                    <Grid container spacing={2}>
                      {onlyTopicsDetails.video_link.map((link, index) => {
                        const embedUrl = getYouTubeEmbedUrl(link);
                        const isYouTube = embedUrl && embedUrl.includes("youtube.com/embed/");
                        return (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            {isYouTube ? (
                              <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0px 2px 8px rgba(0,0,0,0.04)" }}>
                                <iframe
                                  src={embedUrl}
                                  title={`Video Proof ${index + 1}`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%"
                                  }}
                                ></iframe>
                              </Box>
                            ) : (
                              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%" }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: "hidden", mr: 1 }}>
                                  <Icon icon="mdi:video-play-outline" width="20" height="20" style={{ color: "#487FFF" }} />
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 185 }}>{link}</Typography>
                                </Stack>
                                <IconButton size="small" component="a" href={link} target="_blank" rel="noopener noreferrer" sx={{ color: "#487FFF" }}>
                                  <Icon icon="mdi:open-in-new" width="18" height="18" />
                                </IconButton>
                              </Paper>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No video proofs available.</Typography>
                  )}
                </Box>

                {/* Images */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ fontSize: "0.75rem", textTransform: "uppercase", mb: 1.5 }}>
                    2. Proof Images
                  </Typography>
                  {onlyTopicsDetails?.proof_images && onlyTopicsDetails.proof_images.length > 0 ? (
                    <Grid container spacing={1.5}>
                      {onlyTopicsDetails.proof_images.map((img, index) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                          <Box
                            onClick={() => setSelectedImg(getImageUrl(img))}
                            sx={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "100%",
                              borderRadius: "10px",
                              overflow: "hidden",
                              border: "1px solid rgba(0,0,0,0.06)",
                              cursor: "pointer",
                              boxShadow: "0px 2px 6px rgba(0,0,0,0.03)",
                              transition: "transform 0.15s ease",
                              "&:hover": { transform: "scale(1.05)" }
                            }}
                          >
                            <Box
                              component="img"
                              src={getImageUrl(img)}
                              alt={`Proof Image ${index + 1}`}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No proof images uploaded.</Typography>
                  )}
                </Box>

                {/* Documents */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ fontSize: "0.75rem", textTransform: "uppercase", mb: 1.5 }}>
                    3. Proof Documents
                  </Typography>
                  {onlyTopicsDetails?.proof_documents && onlyTopicsDetails.proof_documents.length > 0 ? (
                    <Grid container spacing={2}>
                      {onlyTopicsDetails.proof_documents.map((doc, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: "10px",
                              border: "1px solid rgba(0,0,0,0.06)",
                              background: "#ffffff",
                              transition: "background-color 0.2s",
                              "&:hover": { backgroundColor: "#F8F9FA" },
                              height: "100%"
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: "hidden" }}>
                                <Box sx={{ p: 1, borderRadius: "8px", backgroundColor: "#EF477010", display: "flex", alignItems: "center" }}>
                                  <Icon icon="mdi:file-document-outline" width="20" height="20" style={{ color: "#EF4770" }} />
                                </Box>
                                <Box sx={{ overflow: "hidden" }}>
                                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                                    Document {index + 1}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 160 }}>
                                    {doc.split("/").pop() || "document.pdf"}
                                  </Typography>
                                </Box>
                              </Stack>
                              <IconButton
                                component="a"
                                href={getImageUrl(doc)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: "#487FFF", backgroundColor: "#487FFF10", "&:hover": { backgroundColor: "#487FFF20" } }}
                              >
                                <Icon icon="mdi:eye-outline" width="18" height="18" />
                              </IconButton>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No proof documents available.</Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        {/* Right Column: Advisor details & Verification Proofs */}
        <Grid item xs={12} md={4.5} lg={4}>
          <Stack spacing={3}>
            {/* Advisor Details */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                Advisor Details
              </Typography>
              {advisor ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={advisor.profile_picture ? getImageUrl(advisor.profile_picture) : ""}
                      alt={`${advisor.first_name || ""} ${advisor.last_name || ""}`}
                      sx={{
                        width: 64,
                        height: 64,
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #487FFF 0%, #00C9FF 100%)",
                        boxShadow: "0px 4px 10px rgba(72, 127, 255, 0.15)"
                      }}
                    >
                      {((advisor.first_name?.[0] || "") + (advisor.last_name?.[0] || "")).toUpperCase() || <Icon icon="mdi:user" />}
                    </Avatar>
                    <Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                          {`${advisor.first_name || ""} ${advisor.last_name || ""}`}
                        </Typography>
                        {advisor.verified_status && (
                          <Tooltip title="Verified Advisor">
                            <Icon icon="material-symbols:verified" width="18" height="18" style={{ color: "#487FFF" }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        @{advisor.user_name || "advisor"}
                      </Typography>
                    </Box>
                  </Stack>

                  {advisor.bio && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", lineHeight: 1.5 }}>
                      "{advisor.bio}"
                    </Typography>
                  )}

                  <Divider />

                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon="mdi:email-outline" width="18" height="18" style={{ color: "#666" }} />
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                        {getValue(advisor.email_id)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon="mdi:phone-outline" width="18" height="18" style={{ color: "#666" }} />
                      <Typography variant="body2" color="text.secondary">
                        {advisor.mobile ? `+${advisor.countrycode || "91"} ${advisor.mobile}` : "-"}
                      </Typography>
                    </Stack>
                  </Stack>

                  {advisor.languages && advisor.languages.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1, textTransform: "uppercase" }}>
                        Languages
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {advisor.languages.map((lang, idx) => (
                          <Chip key={idx} label={lang} size="small" sx={{ fontSize: "0.75rem", textTransform: "capitalize", backgroundColor: "#E8EEFF", color: "#487FFF", fontWeight: 600 }} />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No advisor details found.</Typography>
              )}
            </Paper>

            {/* Pricing Configurations */}
            <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                Pricing Configurations
              </Typography>
              {isPricingAvailable ? (
                <Box>
                  {/* Pricing Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
                    <Tabs
                      value={activePricingTab}
                      onChange={(e, val) => setActivePricingTab(val)}
                      aria-label="pricing-tabs"
                      sx={{
                        "& .MuiTab-root": {
                          fontWeight: 600,
                          textTransform: "none",
                          fontSize: "0.875rem",
                          minHeight: 44,
                        }
                      }}
                    >
                      <Tab label="Call" value={1} disabled={!isCallEnabled} />
                      <Tab label="Itinerary" value={2} disabled={!isItineraryEnabled} />
                      <Tab label="Call + Itinerary" value={3} disabled={!isCallItineraryEnabled} />
                    </Tabs>
                  </Box>

                  {/* Tab 1: Call */}
                  {activePricingTab === 1 && isCallEnabled && (
                    <Stack spacing={1.5}>
                      {duration.good_chat_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:chat-processing-outline" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.good_chat_time_duration} Mins Chat Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)} {duration.good_chat_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                      {duration.more_chat_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:chat-processing-outline" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.more_chat_time_duration} Mins Chat Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)} {duration.more_chat_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                      {duration.most_chat_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:chat-processing-outline" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.most_chat_time_duration} Mins Chat Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)} {duration.most_chat_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  )}

                  {/* Tab 2: Itinerary */}
                  {activePricingTab === 2 && isItineraryEnabled && (
                    <Stack spacing={1.5}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Icon icon="mdi:message-text-outline" width="18" height="18" style={{ color: "#487FFF" }} />
                            <Typography variant="body2" fontWeight={500}>Price</Typography>
                          </Stack>
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            {getCurrencySymbol(duration.currency)} {duration.word_price}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  )}

                  {/* Tab 3: Call + Itinerary */}
                  {activePricingTab === 3 && isCallItineraryEnabled && (
                    <Stack spacing={1.5}>
                      {duration.good_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:phone-clock" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.good_time_duration} Mins Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)}{duration.good_time_video_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                      {duration.more_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:phone-clock" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.more_time_duration} Mins Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)}{duration.more_time_video_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                      {duration.most_time_enabled && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.06)" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Icon icon="mdi:phone-clock" width="18" height="18" style={{ color: "#487FFF" }} />
                              <Typography variant="body2" fontWeight={500}>{duration.most_time_duration} Mins Call</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {getCurrencySymbol(duration.currency)}{duration.most_time_video_price}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No pricing details configured.</Typography>
              )}
            </Paper>


          </Stack>
        </Grid>
      </Grid>

      {/* Accept / Reject Dialog */}
      <CommonPopup
        open={popupOpen}
        onClose={() => {
          setPopupOpen(false);
          setPopupType("");
          reset();
        }}
        title={popupType === "accept" ? "Accept Topic" : "Reject Topic"}
        size="sm"
      >
        {popupType === "accept" && (
          <Stack spacing={2.5} sx={{ p: 1 }}>
            <Typography variant="body1">
              Are you sure you want to approve this topic? This will verify and publish the topic.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button variant="outlined" color="inherit" onClick={() => setPopupOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={async () => {
                  await validateTopic({ topic_id: onlyTopicsDetails?.topic_id });
                  setPopupOpen(false);
                  setPopupType("");
                  navigate(-1);
                }}
                startIcon={<Icon icon="mdi:check" />}
              >
                Yes, Approve
              </Button>
            </Stack>
          </Stack>
        )}

        {popupType === "reject" && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2} sx={{ p: 1 }}>
              <FormTextInput
                name={formField.status.name}
                label={formField.status.label}
                control={control}
                validationSchema={{ required: requiredField(formField?.status?.label) }}
                errors={errors?.[formField?.status?.name]?.message}
                required
              />
              <FormTextInput
                name={formField.message.name}
                label={formField.message.label}
                control={control}
                validationSchema={{ required: requiredField(formField?.message?.label) }}
                errors={errors?.[formField?.message?.name]?.message}
                required
              />
              <FormTextInput
                name={formField.next_step.name}
                label={formField.next_step.label}
                control={control}
                validationSchema={{ required: requiredField(formField?.next_step?.label) }}
                errors={errors?.[formField?.next_step?.name]?.message}
                required
              />
              <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button variant="outlined" color="inherit" onClick={() => setPopupOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="error">
                  Reject Topic
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </CommonPopup>

      {/* Image Lightbox Dialog */}
      <CommonPopup
        open={Boolean(selectedImg)}
        onClose={() => setSelectedImg(null)}
        title="Proof Image Preview"
        size="md"
      >
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 1, backgroundColor: "#000000" }}>
          <Box
            component="img"
            src={selectedImg}
            alt="Preview"
            sx={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 1 }}
          />
        </Box>
      </CommonPopup>
    </Box>
  );
};

// Meta Item Helper Component
const MetaItem = ({ icon, label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "flex-start", gap: 1.5, height: "100%" }}>
    <Box sx={{ p: 0.5, borderRadius: "6px", backgroundColor: "#E8EEFF", display: "flex", alignItems: "center" }}>
      <Icon icon={icon} width="16" height="16" style={{ color: "#487FFF" }} />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {value}
      </Typography>
    </Box>
  </Paper>
);

// Card Header Component
const CardHeader = ({ onBack, statusLabel, statusColor, isVerified, onApprove, onReject }) => (
  <Paper sx={{ p: 2, borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.02)" }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton onClick={onBack} sx={{ backgroundColor: "#F4F6F8", "&:hover": { backgroundColor: "#E4E6F0" } }}>
          <Icon icon="mdi:arrow-left" width="20" height="20" />
        </IconButton>
        <Box>
          <Breadcrumbs separator={<Icon icon="mdi:chevron-right" width="14" height="14" />} sx={{ mb: 0.5 }}>
            <Link underline="hover" color="inherit" href="/dashboard" sx={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: 0.5 }}>
              <Icon icon="mdi:home-outline" width="12" height="12" /> Admin
            </Link>
            <Link underline="hover" color="inherit" href="/validateTopics" sx={{ fontSize: "0.75rem" }}>
              Validate Topics
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              Details
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Topic Validation
            </Typography>
            <Chip
              label={statusLabel}
              color={statusColor}
              size="small"
              sx={{ fontWeight: 700, fontSize: "0.7rem", borderRadius: "6px" }}
            />
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <Button
          variant="outlined"
          color="error"
          onClick={onReject}
          startIcon={<Icon icon="ic:round-close" />}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 2.5
          }}
        >
          Reject
        </Button>
        {!isVerified && (
          <Button
            variant="contained"
            color="success"
            onClick={onApprove}
            startIcon={<Icon icon="ic:round-check" />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              boxShadow: "0px 4px 10px rgba(69, 179, 105, 0.2)"
            }}
          >
            Approve
          </Button>
        )}
      </Stack>
    </Stack>
  </Paper>
);

export default ViewTopics;
